import type { DragEvent, KeyboardEvent } from "react";
import { useState } from "react";

interface UseRowDragOptions<T> {
  rows: T[];
  onUpdate: (index: number, field: keyof T, value: unknown) => void;
}

interface RowHandleProps {
  draggable: true;
  onDragStart: (e: DragEvent<HTMLElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void;
}

interface RowDropProps {
  onDragOver: (e: DragEvent<HTMLElement>) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent<HTMLElement>) => void;
}

interface UseRowDragResult {
  dragOverIndex: number | null;
  getHandleProps: (index: number) => RowHandleProps;
  getRowDropProps: (index: number) => RowDropProps;
}

/**
 * Row reorder via HTML5 drag-and-drop, with an arrow-key fallback on the drag
 * handle. Reordering is implemented as a full field swap between the two row
 * indexes (rather than an array-level setter) since callers only expose a
 * per-field onUpdate(index, field, value) callback.
 */
export function useRowDrag<T>({ rows, onUpdate }: UseRowDragOptions<T>): UseRowDragResult {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const swapRows = (a: number, b: number): void => {
    const rowA = rows[a];
    const rowB = rows[b];
    if (!rowA || !rowB) return;
    for (const key of Object.keys(rowA as Record<string, unknown>) as (keyof T)[]) {
      onUpdate(a, key, rowB[key]);
      onUpdate(b, key, rowA[key]);
    }
  };

  const getHandleProps = (index: number): RowHandleProps => ({
    draggable: true,
    onDragStart: (e) => {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));
      setDragIndex(index);
    },
    onKeyDown: (e) => {
      if (e.key === "ArrowUp" && index > 0) {
        e.preventDefault();
        swapRows(index, index - 1);
      } else if (e.key === "ArrowDown" && index < rows.length - 1) {
        e.preventDefault();
        swapRows(index, index + 1);
      }
    },
  });

  const getRowDropProps = (index: number): RowDropProps => ({
    onDragOver: (e) => {
      e.preventDefault();
      if (dragOverIndex !== index) setDragOverIndex(index);
    },
    onDragLeave: () => setDragOverIndex((current) => (current === index ? null : current)),
    onDrop: (e) => {
      e.preventDefault();
      setDragOverIndex(null);
      const from = dragIndex;
      setDragIndex(null);
      if (from === null || from === index) return;
      swapRows(from, index);
    },
  });

  return { dragOverIndex, getHandleProps, getRowDropProps };
}
